import { ApiResponse } from '@/models/ApiResponse';
import {
  DebtSummaryItem,
  FetchIRIndicesSuccessPayload,
  FetchSwapTenorSuccessPayload,
  FetchTradeDebtRequestPayload,
  GetIrMarketEnvironmentDataForChartRequestPayload,
  GetIrMarketEnvironmentDataForChartSuccessPayload,
  TradeSummaryItem,
  UserWidgetResponse,
  ValidateDebtsResponse,
  ValidateIrIndexPayload,
  ValidateIrIndexResponse,
} from '@/pages/IRanalysis/store/types';
import {
  DownloadRMToolsReportPayload,
  GetUserWidgetsPayload,
  SaveUserWidgetDataPayload,
} from '@/pages/IRanalysis/types';
import { GenericResponse } from '@/types/downloadReports';
import { AxiosInstance, AxiosResponse } from 'axios';

export default class IRAnalysisAPI {
  private client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  async getCurrencySwapTenors(): Promise<
    AxiosResponse<FetchSwapTenorSuccessPayload>
  > {
    return await this.client.get('/v2/market-data/currency-swap-tenors/');
  }

  async getIndexList(): Promise<AxiosResponse<FetchIRIndicesSuccessPayload>> {
    return this.client.get('/v2/market-data/index-list/');
  }

  async validateIrIndex(
    payload: ValidateIrIndexPayload,
  ): Promise<AxiosResponse<ValidateIrIndexResponse>> {
    return this.client.post('/v2/market-data/validate-ir-index/', payload);
  }

  async fetchTrades(
    payload: FetchTradeDebtRequestPayload,
  ): Promise<AxiosResponse<ApiResponse<TradeSummaryItem[]>>> {
    return this.client.post(
      '/v2/hedging/transactions/entity-transactions/',
      payload,
    );
  }

  async fetchDebts(
    payload: FetchTradeDebtRequestPayload,
  ): Promise<AxiosResponse<ApiResponse<DebtSummaryItem[]>>> {
    return this.client.post('/v2/debt/entity-debts/', payload);
  }

  async downloadRMToolsReport(
    payload: DownloadRMToolsReportPayload,
  ): Promise<AxiosResponse<GenericResponse>> {
    return this.client.post(
      '/v2/reporting/download-rm-tools-reports/',
      payload,
    );
  }

  async getClosestTradeValuationDateByDate(): Promise<
    AxiosResponse<ApiResponse<string>>
  > {
    return this.client.get('/v2/hedging/closest-trade-valuation-date/');
  }

  async validateTrades(payload: {
    transaction_id_list: number[];
  }): Promise<AxiosResponse<ApiResponse<string[]>>> {
    return this.client.post(
      '/v2/hedging/transactions/active/trades-validation/',
      payload,
    );
  }

  async validateDebts(payload: {
    debt_id_list: number[];
  }): Promise<AxiosResponse<ValidateDebtsResponse>> {
    return this.client.post('/v2/debt/debt-hedge/validate/', payload);
  }

  async saveUserWidgetData(
    payload: SaveUserWidgetDataPayload,
  ): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.client.post('/v2/user/save-user-widget-data/', payload);
  }

  async getUserWidgets(
    payload: GetUserWidgetsPayload,
  ): Promise<AxiosResponse<ApiResponse<ApiResponse<UserWidgetResponse[]>>>> {
    return this.client.post<ApiResponse<ApiResponse<UserWidgetResponse[]>>>(
      '/v2/user/get-user-widgets/',
      payload,
    );
  }

  async getUserWidgetById(
    widget_id: string,
  ): Promise<AxiosResponse<ApiResponse<ApiResponse<UserWidgetResponse>>>> {
    return this.client.post<ApiResponse<ApiResponse<UserWidgetResponse>>>(
      '/v2/user/get-widget-data-by-id/',
      { widget_id },
    );
  }

  async fetchIrMarketEnvironmentDataForChart(
    payload: GetIrMarketEnvironmentDataForChartRequestPayload,
  ): Promise<
    AxiosResponse<ApiResponse<GetIrMarketEnvironmentDataForChartSuccessPayload>>
  > {
    return this.client.post(
      '/v2/reporting/ir-market-environment-data/',
      payload,
    );
  }
}
